import { useTranslation } from "react-i18next";

import {
  EMagistratArchiveCaseState,
  EMagistratArchiveJobStatus,
} from "api/magistratArchiveService";
import Chip from "components/Chip";

import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

type TTone = "done" | "pending" | "idle" | "issues" | "error";

// Kolory z ról palety Nota (bez lokalnych odcieni): zrobione — primary-container,
// w toku — secondary-container, archiwalne/anulowane — surface-container, z problemami — tertiary.
const tones: Record<Exclude<TTone, "error">, { background: string; color: string }> = {
  done: {
    background: "var(--Nota-sys-light-primary-container, #e5deff)",
    color: "var(--Nota-sys-light-on-primary-container, #190064)",
  },
  pending: {
    background: "var(--Nota-sys-light-secondary-container, #e5dff9)",
    color: "var(--Nota-sys-light-on-secondary-container, #1c192b)",
  },
  idle: {
    background: "var(--Nota-sys-light-surface-container, #f1ecf1)",
    color: "var(--Nota-sys-light-on-surface-variant, #48454f)",
  },
  issues: {
    background: "var(--Nota-sys-light-inverse-on-surface, #f4eff4)",
    color: "var(--Nota-sys-light-tertiary, #b8006a)",
  },
};

const jobTone = (status?: EMagistratArchiveJobStatus | null): TTone => {
  switch (status) {
    case EMagistratArchiveJobStatus.Completed:
      return "done";
    case EMagistratArchiveJobStatus.CompletedWithIssues:
      return "issues";
    case EMagistratArchiveJobStatus.Failed:
      return "error";
    case EMagistratArchiveJobStatus.Queued:
    case EMagistratArchiveJobStatus.Running:
      return "pending";
    default:
      return "idle";
  }
};

/** Pigułka stanu teczki archiwum Magistratu albo zlecenia jej importu. */
const MagistratArchiveStateChip: FC<Props> = ({ state, jobStatus }) => {
  const { t } = useTranslation();

  let tone: TTone;
  let label: string;
  if (state === undefined) {
    tone = jobTone(jobStatus);
    label = t(`settings.archive.jobStatus.${jobStatus ?? 0}`);
  } else if (state === EMagistratArchiveCaseState.Imported) {
    tone = "done";
    label = t("settings.archive.state.imported");
  } else if (state === EMagistratArchiveCaseState.Queued) {
    tone = "pending";
    label =
      jobStatus === EMagistratArchiveJobStatus.Running
        ? t("settings.archive.jobStatus.1")
        : t("settings.archive.state.queued");
  } else if (jobStatus === EMagistratArchiveJobStatus.Failed) {
    tone = "error";
    label = t("settings.archive.state.failed");
  } else {
    tone = "idle";
    label = t("settings.archive.state.archived");
  }

  return (
    <span className={styles.wrapper}>
      {tone === "error" ? (
        <Chip small error element={<>{label}</>} />
      ) : (
        <Chip
          small
          element={<>{label}</>}
          background={tones[tone].background}
          color={tones[tone].color}
        />
      )}
    </span>
  );
};

export default MagistratArchiveStateChip;
